import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * The small, style-only primitives. Each one is a direct port of a block in the
 * spec's `renderVals()`; the pinned numbers live in `theme/theme.css` and
 * `theme/tokens.ts`, never inline here.
 */

/**
 * A section caption. Static by default; a `<button>` when `onClick` is given.
 *
 * The same static-vs-button split `Row` makes, and for the same reason: a
 * heading that does something has to be reachable by keyboard and announced as
 * actionable, and a `<div onClick>` is neither. The three callers that pass no
 * handler (ShareScreen, SettingsScreen, OverviewScreen) render byte-identical
 * markup to before, so nothing gains a tab stop it did not ask for.
 */
export function SectionHeader({ children, onClick, label }: {
  children: ReactNode;
  onClick?: () => void;
  /** Accessible name for the button form — the caption alone is rarely enough. */
  label?: string;
}) {
  if (!onClick) return <div className="section-header">{children}</div>;

  return (
    <button
      type="button"
      className="section-header section-header--button"
      aria-label={label}
      onClick={onClick}
    >
      {/* Wrapped rather than bare so the caption stays its own text node: the
          glyph below would otherwise join it in the button's text content, and
          "DAIRY" would only be findable as "DAIRY✎". */}
      <span className="section-header__label">{children}</span>
      {/* Without this there is nothing to tell anyone the caption is tappable —
          a heading does not read as a control, and on a phone there is no hover
          state to discover it with. Decorative: the name comes from `label`. */}
      <span className="section-header__hint" aria-hidden="true">
        ✎
      </span>
    </button>
  );
}

export function GroupCard({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={className ? `group-card ${className}` : 'group-card'}>{children}</div>;
}

interface RowProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Rendered next to the title — the role badge on shared lists. */
  badge?: ReactNode;
  chevron?: boolean;
  onClick?: () => void;
}

export function Row({ title, subtitle, badge, chevron = true, onClick }: RowProps) {
  const body = (
    <>
      <span className="row__main">
        {badge ? (
          <span className="row__titleLine">
            <span className="row__title">{title}</span>
            {badge}
          </span>
        ) : (
          <span className="row__title">{title}</span>
        )}
        {subtitle ? <span className="row__subtitle">{subtitle}</span> : null}
      </span>
      {chevron ? <span className="row__chevron">›</span> : null}
    </>
  );

  if (!onClick) return <div className="row row--static">{body}</div>;

  return (
    <button type="button" className="row row--button" onClick={onClick}>
      {body}
    </button>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      className={checked ? 'checkbox is-checked' : 'checkbox'}
      onClick={onChange}
    >
      {checked ? '✓' : ''}
    </button>
  );
}

/**
 * A single-choice row inside a `GroupCard` — the platform pattern for picking
 * one of a few options, with a trailing checkmark on the selected one.
 *
 * `role="radio"` rather than a styled `<input>`: the rows need the same
 * separator/padding geometry as every other settings row, and a native radio
 * brings its own box that cannot be reconciled with it. The a11y contract is
 * carried by the roles instead — the group wrapper supplies `radiogroup`.
 */
export function OptionRow({
  label,
  hint,
  swatch,
  selected,
  onSelect,
}: {
  label: ReactNode;
  hint?: ReactNode;
  /** Optional colour dot, used by the style picker to preview each family. */
  swatch?: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={selected ? 'option-row is-selected' : 'option-row'}
      onClick={onSelect}
    >
      {swatch ? (
        <span className="option-row__swatch" style={{ background: swatch }} aria-hidden="true" />
      ) : null}
      <span className="option-row__main">
        <span className="option-row__label">{label}</span>
        {hint ? <span className="option-row__hint">{hint}</span> : null}
      </span>
      <span className="option-row__check" aria-hidden="true">
        {selected ? '✓' : ''}
      </span>
    </button>
  );
}

/** Explanatory text under a `GroupCard`, in the platform's footnote style. */
export function SectionFootnote({ children }: { children: ReactNode }) {
  return <p className="section-footnote">{children}</p>;
}

export function Chip({
  children,
  variant,
}: {
  children: ReactNode;
  variant?: 'badge' | 'role' | 'muted';
}) {
  const className = variant ? `chip chip--${variant}` : 'chip';
  return <span className={className}>{children}</span>;
}

export function EmptyState({
  glyph,
  title,
  body,
  action,
  small,
}: {
  glyph: string;
  title: string;
  body: ReactNode;
  action?: { label: string; onClick: () => void };
  small?: boolean;
}) {
  return (
    <div className="empty-state">
      <div className={small ? 'empty-state__glyph empty-state__glyph--sm' : 'empty-state__glyph'}>
        {glyph}
      </div>
      <div className="empty-state__title">{title}</div>
      <div className="empty-state__body">{body}</div>
      {action ? (
        <button type="button" className="empty-state__action" onClick={action.onClick}>
          {action.label}
        </button>
      ) : null}
    </div>
  );
}

export function Skeleton({ width, height = 13 }: { width: string; height?: number }) {
  return <div className="skeleton" style={{ width, height }} />;
}

/** Widths are the spec's `skeletonRows`. */
const SKELETON_ROWS = [
  { w1: '55%', w2: '32%' },
  { w1: '40%', w2: '28%' },
  { w1: '62%', w2: '30%' },
  { w1: '48%', w2: '26%' },
];

export function ListSkeleton() {
  return (
    <div style={{ padding: '14px 16px' }}>
      <div className="skeleton" style={{ height: 13, width: 90, margin: '12px 4px' }} />
      <div className="skeleton-card">
        {SKELETON_ROWS.map((row, i) => (
          <div key={i} className="skeleton-row">
            <div style={{ flex: 1 }}>
              <Skeleton width={row.w1} />
              <div style={{ height: 8 }} />
              <Skeleton width={row.w2} height={10} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ItemsSkeleton() {
  return (
    <div style={{ padding: '16px 12px' }}>
      <div className="skeleton-card">
        {SKELETON_ROWS.map((row, i) => (
          <div key={i} className="skeleton-row" style={{ gap: 12, padding: '14px 16px' }}>
            <div className="skeleton" style={{ width: 22, height: 22, borderRadius: '50%' }} />
            <Skeleton width={row.w1} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ErrorBanner({ message, onRetry }: { message?: string; onRetry: () => void }) {
  return (
    <div className="error-banner" role="alert">
      <span style={{ fontSize: 15 }}>⚠︎</span>
      <span style={{ flex: 1 }}>{message ?? "Couldn't reach the server."}</span>
      <button type="button" className="error-banner__retry" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

/**
 * The terminal state for a 403 / 404 from the API.
 *
 * Distinct from `ErrorBanner` on purpose: there is nothing to retry, so there
 * is no Retry button and no skeleton underneath. Access is enforced per list in
 * `service.py` against client-supplied ids, so this is a state the UI must be
 * able to reach — the plan's "surface 403s as a real message rather than an
 * infinite skeleton".
 *
 * It also never names the resource. A list you have no role on is supposed to
 * be invisible, and echoing back "Groceries" would confirm it exists.
 */
export function AccessNotice({
  kind,
  onBack,
}: {
  kind: 'forbidden' | 'missing';
  onBack?: () => void;
}) {
  return (
    <div className="empty-state" role="alert">
      <div className="empty-state__glyph">{kind === 'forbidden' ? '🔒' : '∅'}</div>
      <div className="empty-state__title">
        {kind === 'forbidden' ? 'Not allowed' : 'List unavailable'}
      </div>
      <div className="empty-state__body">
        {kind === 'forbidden'
          ? "You can look at this list, but you can't change it."
          : "This list no longer exists, or it isn't shared with you."}
      </div>
      {onBack ? (
        <button type="button" className="empty-state__action" onClick={onBack}>
          Back to your lists
        </button>
      ) : null}
    </div>
  );
}

export function FAB(props: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" aria-label="New list" {...props} className="fab">
      +
    </button>
  );
}
